import React, { useMemo } from 'react';
import { Col, Row } from 'antd';
import { FormikProps } from 'formik';
import { Input } from 'formik-antd';

import FormItem from 'components/formik/FormItem';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import { useAppSelector } from 'hooks/useAppSelector';
import {
  countriesSelector,
  countryCitiesSelector,
  isCountriesLoadingSelector,
  isCountryCitiesLoadingSelector,
} from 'redux/selectors/regionSelectors';
import { CompanyFormValues } from '../CompanyModal';

type Props = {
  formik: FormikProps<CompanyFormValues>;
};

export default function InternationalCompanyStateSection(props: Props) {
  const { formik } = props;

  const countries = useAppSelector(countriesSelector);
  const isCountriesLoading = useAppSelector(isCountriesLoadingSelector);

  const countryCities = useAppSelector(countryCitiesSelector);
  const isCountryCitiesLoading = useAppSelector(isCountryCitiesLoadingSelector);

  const filteredInternationalCities = useMemo(
    () =>
      countryCities.filter((city) => city.countryId === formik.values.country),
    [countryCities, formik.values.country],
  );

  const handleCountryChange = (
    countryId: number,
    formik: FormikProps<CompanyFormValues>,
  ) => {
    formik.setFieldValue('city', '', false);
    formik.setFieldValue('state', undefined, false);
    formik.setFieldValue('internationalCityName', '', false);
    formik.setFieldValue('internationalStateName', '', false);
  };

  return (
    <>
      <Row gutter={40}>
        <Col xs={24} md={12}>
          <Row gutter={20}>
            <Col xs={24} lg={12}>
              <FormItem name="country" label="Country *">
                <FormikSelectWithLoading
                  isLoading={isCountriesLoading}
                  name="country"
                  showSearch
                  placeholder="Select country"
                  optionFilterProp="children"
                  onChange={(value: number) =>
                    handleCountryChange(value, formik)
                  }
                >
                  {countries.map((country) => (
                    <FormikSelectWithLoading.Option
                      key={`select-country-${country.id}`}
                      value={country.id || -1}
                    >
                      {country.name}
                    </FormikSelectWithLoading.Option>
                  ))}
                </FormikSelectWithLoading>
              </FormItem>
            </Col>

            <Col xs={24} lg={12}>
              <FormItem name="internationalStateName" label="State *">
                <Input
                  name="internationalStateName"
                  placeholder="Enter state"
                  disabled={!formik.values.country}
                  autoComplete="off"
                />
              </FormItem>
            </Col>

            <Col xs={24} lg={12}>
              <FormItem label="ZIP" name="zip">
                <Input
                  name="zip"
                  placeholder="Enter ZIP code"
                  autoComplete="off"
                />
              </FormItem>
            </Col>
          </Row>
        </Col>
        <Col xs={24} md={12}>
          <Row gutter={20}>
            <Col xs={24} lg={12}>
              <FormItem name="internationalCityName" label="City *">
                <FormikAutoCompleteWithLoading
                  isLoading={isCountryCitiesLoading}
                  name="internationalCityName"
                  filterOption
                  placeholder="Enter city"
                  showSearch
                  disabled={!formik.values.internationalStateName}
                >
                  {filteredInternationalCities.map((city) => (
                    <FormikAutoCompleteWithLoading.Option
                      key={`company-city-${city.id}`}
                      value={city.name || ''}
                    >
                      {city.name}
                    </FormikAutoCompleteWithLoading.Option>
                  ))}
                </FormikAutoCompleteWithLoading>
              </FormItem>
            </Col>

            <Col xs={24} lg={12}>
              <FormItem label="Address *" name="address">
                <Input
                  name="address"
                  placeholder="Enter address"
                  autoComplete="off"
                />
              </FormItem>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
}
